import React, { FC, useMemo, useEffect, ReactNode } from 'react';
import { T } from 'ia-react-core/~/interfaces/t';

import { SCP } from 'ia-react-core/~/interfaces/SCP';
import { StyledBadge, StyledBadgeText } from './Badge.styles';

export interface BadgeProps extends SCP<'div'> {
  children?: string | ReactNode | number;
  /** Type of the type for accessibility. By default "Notification". */
  badgeType?: string;
  /** Text to indicate the notication are unread. */
  unreadText?: string;
  /** Max number to display, after witch {{maxCount}}+ is displayed. */
  maxCount?: number;
  /** Callback triggered when the badge changes. */
  onBadgeChange?: (params: {
    count: ReactNode;
    content: ReactNode;
    label: string;
  }) => void;
}

interface BadgeInternalProps {
  t: T;
  tReady: boolean;
}

interface BadgeComposedProps extends BadgeProps, BadgeInternalProps {

}

const BadgeDefaultProps = {
  maxCount: 99,
};

const Badge: FC<BadgeComposedProps> = ({ badgeType, maxCount, t, tReady, children, onBadgeChange, ...props }) => {
  const resolvedBadgeType = useMemo(() => badgeType || t('badgeType'), [badgeType, t]);
  const isNumberBadge = useMemo(() => typeof children === 'number' || String(Number(children)) === children, [children]);
  const count = useMemo(() => Number(children), [children]);

  const resolvedContent = useMemo(() => {
    if (isNumberBadge && count > maxCount) return `${maxCount}+`;
    return children;
  }, [children, count, isNumberBadge, maxCount]);
  const label = useMemo(() => {
    if (isNumberBadge) return `${resolvedBadgeType}, ${t('unread', { count: resolvedContent })}`;
    return '';
  }, [isNumberBadge, resolvedBadgeType, resolvedContent, t]);

  const charLength = useMemo(() => String(resolvedContent).length, [resolvedContent]);

  useEffect(() => {
    if (onBadgeChange) {
      onBadgeChange({
        count,
        content: resolvedContent,
        label,
      });
    }
  }, [count, label, onBadgeChange, resolvedContent]);

  if (resolvedContent !== '0') {
    return (
      <StyledBadge aria-label={label} data-testid="Badge" {...props} charLength={charLength}>
        <StyledBadgeText charLength={charLength}>
          {resolvedContent}
        </StyledBadgeText>
      </StyledBadge>
    );
  }
  return (null);
};

Badge.defaultProps = BadgeDefaultProps;

export default Badge;
